import Phaser from 'phaser';
import { WaveSpawner } from '../systems/WaveSpawner';
import { Monster, MonsterType } from '../entities/Monster';
import { COLORS } from '../constants';
import { RENDER_LAYERS } from '../systems/RenderSystem';

// ============================================
// Wave Preview UI - Lower Left Panel
// Based on wave_preview_and_coin_spill.md
// ============================================

const MONSTER_COLORS: Record<MonsterType, number> = {
  crawler: 0xff4444,
  runner: 0xffff44,
  brute: 0x8844ff,
  shielded: 0x888888,
  bat: 0x444444,
  flyer: 0x6666ff,
  swarm: 0x66ff66,
  juggernaut: 0xff00ff,
  phased: 0x00ffff,
};

const MONSTER_NAMES: Record<MonsterType, string> = {
  crawler: 'Crawler',
  runner: 'Runner',
  brute: 'Brute',
  shielded: 'Shielded',
  bat: 'Bat',
  flyer: 'Flyer',
  swarm: 'Swarm',
  juggernaut: 'Juggernaut',
  phased: 'Phased',
};

type MonsterIcon = Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image;

interface MonsterIconData {
  icon: MonsterIcon;
  type: MonsterType;
  frame: number;
  frameCount: number;
  timer: number;
}

const PREVIEW_PANEL_WIDTH = 280;
const PREVIEW_PANEL_HEIGHT = 130;
const PREVIEW_ICON_SIZE = 44;
const PREVIEW_ANIMATION_MS = 200;

export class WavePreviewUI extends Phaser.GameObjects.Container {
  private panel: Phaser.GameObjects.Rectangle;
  private panelBorder: Phaser.GameObjects.Rectangle;
  private monsterIconData: MonsterIconData[] = [];
  private monsterName: Phaser.GameObjects.Text;
  private waveLabel: Phaser.GameObjects.Text;
  private countdownText: Phaser.GameObjects.Text;
  private progressBarBg: Phaser.GameObjects.Rectangle;
  private progressBar: Phaser.GameObjects.Rectangle;
  private traitIcons: Phaser.GameObjects.Text;

  // Boss styling
  private isBossWave: boolean = false;
  
  // Track current monsters for efficient updates
  private currentMonsterTypes: string[] = [];

  constructor(scene: Phaser.Scene) {
    super(scene, 20, scene.scale.height - PREVIEW_PANEL_HEIGHT - 20);

    // Panel background - wider for bigger animated icons
    this.panel = scene.add.rectangle(0, 0, PREVIEW_PANEL_WIDTH, PREVIEW_PANEL_HEIGHT, COLORS.BG_MEDIUM, 0.85);
    this.panel.setOrigin(0, 0);
    this.add(this.panel);

    // Panel border
    this.panelBorder = scene.add.rectangle(0, 0, PREVIEW_PANEL_WIDTH, PREVIEW_PANEL_HEIGHT);
    this.panelBorder.setOrigin(0, 0);
    this.panelBorder.setStrokeStyle(2, COLORS.PRIMARY);
    this.panelBorder.setFillStyle(0x000000, 0);
    this.add(this.panelBorder);

    // Monster name (right of icons)
    const textLeft = 14 + PREVIEW_ICON_SIZE + 10;
    this.monsterName = scene.add.text(textLeft, 10, 'Next: Crawler', {
      fontSize: '13px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.add(this.monsterName);

    // Wave label
    this.waveLabel = scene.add.text(textLeft, 28, 'Wave 1', {
      fontSize: '11px',
      color: '#aaaaaa',
    });
    this.add(this.waveLabel);

    // Trait icons (air/shielded/boss indicators)
    this.traitIcons = scene.add.text(textLeft, 44, '', {
      fontSize: '12px',
    });
    this.add(this.traitIcons);

    // Countdown text
    this.countdownText = scene.add.text(14, 68, 'Incoming in 20s', {
      fontSize: '14px',
      color: '#ffcc00',
      fontStyle: 'bold',
    });
    this.add(this.countdownText);

    // Progress bar
    const barWidth = PREVIEW_PANEL_WIDTH - 28;
    this.progressBarBg = scene.add.rectangle(14, 92, barWidth, 12, 0x333333);
    this.progressBarBg.setOrigin(0, 0);
    this.add(this.progressBarBg);

    this.progressBar = scene.add.rectangle(14, 92, barWidth, 12, COLORS.SECONDARY);
    this.progressBar.setOrigin(0, 0);
    this.add(this.progressBar);

    // Set up UI layer
    this.setScrollFactor(0);
    this.setDepth(RENDER_LAYERS.UI_SCREEN);

    scene.add.existing(this);
  }

  private getFrameCount(type: MonsterType): number {
    let n = 1;
    while (this.scene.textures.exists(`monster-${type}-${n + 1}`)) n++;
    return n;
  }

  update(): void {
    const preview = WaveSpawner.getNextWavePreview();

    if (!preview) {
      // No next wave (game over or victory)
      this.setVisible(false);
      return;
    }

    this.setVisible(true);

    // Get unique monster types for this wave
    const uniqueMonsterTypes = [...new Set(preview.monsterTypes)] as MonsterType[];
    
    // Update monster icons if types changed
    if (!this.arraysEqual(uniqueMonsterTypes, this.currentMonsterTypes)) {
      this.updateMonsterIcons(uniqueMonsterTypes);
      this.currentMonsterTypes = [...uniqueMonsterTypes];
    }

    // Build monster name list
    const monsterNames = uniqueMonsterTypes.map(type => MONSTER_NAMES[type] || 'Unknown');
    
    // Boss wave styling
    if (preview.isBoss) {
      this.setBossMode(true, preview.bossType || 'Boss');
    } else {
      this.setBossMode(false);
      if (monsterNames.length === 1) {
        this.monsterName.setText(`Next: ${monsterNames[0]}`);
      } else if (monsterNames.length === 2) {
        this.monsterName.setText(`Next: ${monsterNames.join(', ')}`);
      } else {
        this.monsterName.setText(`Next: ${monsterNames.slice(0, 2).join(', ')}...`);
      }
    }

    this.waveLabel.setText(`Wave ${preview.waveIndex}`);

    // Trait icons - show any special traits from any monster in wave
    let traits = '';
    for (const monsterType of uniqueMonsterTypes) {
      const stats = Monster.getStats(monsterType);
      if (stats.movementType === 'air' && !traits.includes('✈️')) traits += '✈️ ';
      if (stats.arrowResist && !traits.includes('🛡️')) traits += '🛡️ ';
      if (stats.splashResist && !traits.includes('💥')) traits += '💥 ';
      if (stats.slowImmune && !traits.includes('⚡')) traits += '⚡ ';
    }
    this.traitIcons.setText(traits);

    // Countdown text - show seconds until wave arrives
    const seconds = Math.ceil(preview.startInSeconds);
    if (seconds > 0) {
      this.countdownText.setText(`⚠️ Incoming in ${seconds}s`);
      this.countdownText.setColor('#ffcc00');
    } else {
      this.countdownText.setText('⚔️ WAVE ACTIVE!');
      this.countdownText.setColor('#ff4444');
    }

    // Progress bar - always show countdown
    const progress = 1 - (preview.startInSeconds / Math.max(preview.totalCountdown, 1));
    const clampedProgress = Phaser.Math.Clamp(progress, 0, 1);
    const barWidth = PREVIEW_PANEL_WIDTH - 28;
    this.progressBar.setSize(barWidth * clampedProgress, 12);
    this.progressBar.setVisible(true);
    this.progressBarBg.setVisible(true);

    // Animate monster icons (cycle frames)
    const dt = this.scene.game.loop.delta;
    for (const data of this.monsterIconData) {
      if (data.frameCount <= 1) continue;
      data.timer += dt;
      if (data.timer >= PREVIEW_ANIMATION_MS) {
        data.timer = 0;
        data.frame = (data.frame + 1) % data.frameCount;
        const frameKey = `monster-${data.type}-${data.frame + 1}`;
        if (data.icon instanceof Phaser.GameObjects.Image && this.scene.textures.exists(frameKey)) {
          data.icon.setTexture(frameKey);
        }
      }
    }
  }

  private updateMonsterIcons(monsterTypes: MonsterType[]): void {
    // Clear existing
    this.monsterIconData.forEach(d => d.icon.destroy());
    this.monsterIconData = [];

    const iconSize = PREVIEW_ICON_SIZE;
    const spacing = iconSize + 6;
    const startY = 12;
    const imageKey = (type: MonsterType, frame: number) => `monster-${type}-${frame + 1}`;

    monsterTypes.forEach((type, index) => {
      const x = 14;
      const y = startY + index * spacing;
      const frameCount = this.getFrameCount(type);
      const hasImage = this.scene.textures.exists(imageKey(type, 0));

      if (hasImage) {
        const icon = this.scene.add.image(x, y, imageKey(type, 0));
        icon.setOrigin(0, 0);
        icon.setDisplaySize(iconSize, iconSize);
        this.add(icon);
        this.monsterIconData.push({
          icon,
          type,
          frame: 0,
          frameCount,
          timer: 0,
        });
      } else {
        const color = MONSTER_COLORS[type] || 0xff4444;
        const icon = this.scene.add.rectangle(x, y, iconSize, iconSize, color);
        icon.setOrigin(0, 0);
        this.add(icon);
        this.monsterIconData.push({
          icon,
          type,
          frame: 0,
          frameCount: 1,
          timer: 0,
        });
      }
    });
  }

  private arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  private setBossMode(isBoss: boolean, bossName?: string): void {
    if (isBoss !== this.isBossWave) {
      this.isBossWave = isBoss;

      if (isBoss) {
        this.panelBorder.setStrokeStyle(3, 0x9932cc); // Purple
        this.monsterName.setText(`BOSS: ${bossName}`);
        this.monsterName.setColor('#ff88ff');
        // Make all icons purple for boss wave (rectangle = fill, image = tint)
        this.monsterIconData.forEach(({ icon }) => {
          if ('setFillStyle' in icon) {
            (icon as Phaser.GameObjects.Rectangle).setFillStyle(0x9932cc);
          } else {
            (icon as Phaser.GameObjects.Image).setTint(0x9932cc);
          }
        });
        this.progressBar.setFillStyle(0x9932cc);
        this.countdownText.setColor('#ff88ff');
      } else {
        this.panelBorder.setStrokeStyle(2, COLORS.PRIMARY);
        this.monsterName.setColor('#ffffff');
        this.progressBar.setFillStyle(COLORS.SECONDARY);
        // Clear tint on image icons so they show normal colors
        this.monsterIconData.forEach(({ icon }) => {
          if ('setTint' in icon) {
            (icon as Phaser.GameObjects.Image).clearTint();
          }
        });
      }
    } else if (isBoss && bossName) {
      this.monsterName.setText(`BOSS: ${bossName}`);
    }
  }

  destroy(): void {
    super.destroy();
  }
}
